import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { RemoveScroll } from 'react-remove-scroll';
import { useKeyboardShortcuts } from 'hooks/useKeyboardShortcuts/useKeyboardShortcuts';
import SVGInline from 'components/SVGInline/SVGInline';
import { useSwipeable } from 'react-swipeable';
import { IconClear } from 'components/Header/HeaderSearch/HeaderSearch.css';
import icons from 'style';
import { useResizeWatcher } from 'hooks/useResizeWatcher/useResizeWatcher';
import { useModalRoot } from 'hooks/usePortalRoot/usePortalRoot';
import { matchIsMobileDevice } from 'style/styled-components/cssMediaQueries';
import {
    DropdownContent,
    DropdownTitle,
    DropdownItemSimpleWrapper,
    DropdownItemWithSvgWrapper,
    DropdownLabel,
    DropdownMenuList,
    DropdownWrapper,
    SVGWrapper,
    MenuContent,
    ExpendableBarWrapper,
    ExpendableBar,
    RightSvgWrapper,
} from './DropdownMenu.css';

export type DropdownMenuProps = {
    keepOpen?: boolean;
    onMouseEnterCb?: () => void;
    onMouseLeaveCb?: () => void;
    showMenuContent?: boolean;
    onClick?: () => void;
    triggerButton: ReactNode;
    verticalPosition?: 'top' | 'bottom';
    horizontalPosition?: 'left' | 'right';
    maxHeight?: number;
    openOnMouseEnter?: boolean;
    selectedIndex?: number;
    stripMobilePortal?: boolean;
    triggerEventName?: string;
    visibilityChangeCallback?: (isVisible: boolean) => void;
    title?: string;
    ignoreInsideClick?: boolean;
    closeOnOutsideClickOnly?: boolean;
    children?: ReactNode;
};

const MOUSE_LEAVE_TIMEOUT_AMOUNT = 500;
const INTERACTIVE_TARGETS = 'a, button, input, [role="button"]';
const TRIGGER_TARGETS = 'button, a[href], [role="button"], [tabindex]:not([tabindex="-1"])';

export const PLAYER_SETTINGS_DROPDOWN_EVENT = 'player-settings-trigger';
export const PLAYER_EPISODES_DROPDOWN_EVENT = 'player-episodes-trigger';

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
    keepOpen = false,
    onMouseEnterCb = () => {},
    onMouseLeaveCb = () => {},
    showMenuContent = false,
    openOnMouseEnter = false,
    selectedIndex = null,
    onClick = () => {},
    children,
    triggerButton,
    verticalPosition,
    horizontalPosition,
    maxHeight,
    stripMobilePortal = false,
    triggerEventName,
    visibilityChangeCallback,
    title,
    ignoreInsideClick = false,
    closeOnOutsideClickOnly = false,
    ...rest
}) => {
    const modalRoot = useModalRoot();
    const [expandedDropdown, setExpandedDropdown] = useState<boolean>(false);
    const [canExpand, setCanExpand] = useState<boolean>(false);
    const [isDropdownMenuOpen, setIsDropdownMenuOpen] = useState<boolean>(showMenuContent);
    const [autoVerticalPosition, setDropdownVerticalPosition] = useState<string>(null);
    const [autoHorizontalPosition, setDropdownHorizontalPosition] = useState<string>(null);
    const [scrollHeight, setScrollHeight] = useState<string>(null);

    const hideAnimationTimeout = useRef(null);
    const triggerClickTimeout = useRef(null);
    const isTriggerClickHandled = useRef(false);
    const outsideClickBlocker = useRef<(event: MouseEvent) => void>(null);
    const menuButtonRef = useRef<HTMLElement>(null);
    const menuContentRef = useRef<HTMLElement>(null);

    const { isMobile, isMobileLandscape, resolution } = useResizeWatcher();

    const setDropdownPosition = () => {
        if (!isMobile() && window && menuButtonRef?.current && menuContentRef?.current) {
            setDropdownVerticalPosition(
                menuButtonRef.current.getBoundingClientRect().bottom + menuContentRef.current.getBoundingClientRect().height <
                    window.innerHeight
                    ? 'bottom'
                    : 'top'
            );

            setDropdownHorizontalPosition(
                menuButtonRef.current.getBoundingClientRect().left + menuContentRef.current.getBoundingClientRect().width <
                    window.innerWidth
                    ? 'left'
                    : 'right'
            );
        }
    };

    const markTriggerClickHandled = () => {
        isTriggerClickHandled.current = true;
        clearTimeout(triggerClickTimeout.current);

        triggerClickTimeout.current = window.setTimeout(() => {
            isTriggerClickHandled.current = false;
        }, 0);
    };

    const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (onClick) {
            onClick();
        }
        markTriggerClickHandled();
        setDropdownPosition();

        const target = e.target as Node;

        if (isDropdownMenuOpen && ignoreInsideClick) {
            if (!menuContentRef.current?.contains(target)) setIsDropdownMenuOpen(false);
            return;
        }
        if (!keepOpen) {
            setIsDropdownMenuOpen(!isDropdownMenuOpen);
        }
    };

    const handleMouseEnter = () => {
        onMouseEnterCb();

        if (keepOpen) return;
        clearTimeout(hideAnimationTimeout.current);

        if (openOnMouseEnter) {
            setDropdownPosition();
            setIsDropdownMenuOpen(true);
        }
    };

    const scheduleClose = () => {
        hideAnimationTimeout.current = window.setTimeout(() => {
            setIsDropdownMenuOpen(false);
        }, MOUSE_LEAVE_TIMEOUT_AMOUNT);
    };

    const handleMouseLeave = () => {
        onMouseLeaveCb();

        if (keepOpen || closeOnOutsideClickOnly) return;
        scheduleClose();
    };

    useKeyboardShortcuts({
        backspace: () => {
            if (keepOpen) return;
            scheduleClose();
        },
    });

    const onSwipeUp = () => {
        if (!canExpand) return;
        setExpandedDropdown(true);
    };

    const onSwipeDown = () => {
        if (!expandedDropdown) {
            setIsDropdownMenuOpen(false);
        }
    };

    const swipeHandlers = useSwipeable({
        onSwipedUp: onSwipeUp,
        onSwipedDown: onSwipeDown,
    });

    useEffect(() => {
        if (!triggerEventName) {
            return undefined;
        }

        const handleCustomEvent = () => {
            if (isTriggerClickHandled.current) {
                return;
            }

            setDropdownPosition();
            setIsDropdownMenuOpen((isOpen) => !isOpen);
        };

        document.addEventListener(triggerEventName, handleCustomEvent);
        return () => {
            document.removeEventListener(triggerEventName, handleCustomEvent);
        };
    }, [triggerEventName]);

    const handleFocusOut = (event) => {
        if (closeOnOutsideClickOnly && !event.relatedTarget) {
            return;
        }

        if (!menuContentRef.current?.contains(event.relatedTarget)) {
            setIsDropdownMenuOpen(false);
        }
    };

    const clearOutsideClickBlocker = () => {
        if (outsideClickBlocker.current) {
            document.removeEventListener('click', outsideClickBlocker.current, true);
            outsideClickBlocker.current = null;
        }
    };

    const blockDismissingClick = () => {
        clearOutsideClickBlocker();

        outsideClickBlocker.current = (event: MouseEvent) => {
            outsideClickBlocker.current = null;
            event.stopPropagation();
        };

        document.addEventListener('click', outsideClickBlocker.current, { capture: true, once: true });
    };

    useEffect(() => {
        if (!closeOnOutsideClickOnly || !isDropdownMenuOpen) {
            return undefined;
        }

        const handleOutsideClick = (event: PointerEvent) => {
            const target = event.target as Node;
            const triggerControl = menuButtonRef.current?.closest(INTERACTIVE_TARGETS) ?? menuButtonRef.current;

            if (triggerControl?.contains(target) || menuContentRef.current?.contains(target)) {
                return;
            }

            if (!(target instanceof Element) || !target.closest(INTERACTIVE_TARGETS)) {
                blockDismissingClick();
            }

            setIsDropdownMenuOpen(false);
        };

        document.addEventListener('pointerdown', handleOutsideClick, true);

        return () => {
            document.removeEventListener('pointerdown', handleOutsideClick, true);
        };
    }, [closeOnOutsideClickOnly, isDropdownMenuOpen]);

    const getDesktopView = () => (
        <DropdownContent
            showMenuContent={isDropdownMenuOpen}
            inert={!isDropdownMenuOpen}
            isMqMobile={isMobile() || isMobileLandscape()}
            verticalPosition={verticalPosition || autoVerticalPosition}
            horizontalPosition={horizontalPosition || autoHorizontalPosition}
        >
            <DropdownMenuList
                ref={(ref) => {
                    menuContentRef.current = ref;
                }}
                onBlur={handleFocusOut}
                style={{ maxHeight: maxHeight ? `${maxHeight}px` : undefined }}
            >
                {children}
            </DropdownMenuList>
        </DropdownContent>
    );

    const getMobileView = () => {
        const content = (
            <RemoveScroll enabled={isDropdownMenuOpen}>
                <div {...swipeHandlers}>
                    <DropdownContent
                        showMenuContent={isDropdownMenuOpen}
                        inert={!isDropdownMenuOpen}
                        isMqMobile={isMobile() || isMobileLandscape()}
                        verticalPosition={verticalPosition || autoVerticalPosition}
                        horizontalPosition={horizontalPosition || autoHorizontalPosition}
                    >
                        <DropdownMenuList>
                            {!expandedDropdown && canExpand && (
                                <ExpendableBarWrapper>
                                    <ExpendableBar />
                                </ExpendableBarWrapper>
                            )}
                            {(title || expandedDropdown) && (
                                <DropdownTitle
                                    showBottomBorder={!!title || expandedDropdown}
                                    selected={false}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                >
                                    {title}
                                    {expandedDropdown && (
                                        <IconClear
                                            className={'clear-icon'}
                                            svg={icons.closeIcon}
                                            onClick={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                setExpandedDropdown(false);
                                            }}
                                        />
                                    )}
                                </DropdownTitle>
                            )}
                            <MenuContent
                                ref={(ref) => {
                                    menuContentRef.current = ref;
                                }}
                                style={{
                                    height: scrollHeight ?? undefined,
                                    overflowY: expandedDropdown || !matchIsMobileDevice() ? 'auto' : 'hidden',
                                }}
                            >
                                {children}
                            </MenuContent>
                        </DropdownMenuList>
                    </DropdownContent>
                </div>
            </RemoveScroll>
        );

        if (!stripMobilePortal) {
            return createPortal(content, modalRoot);
        }

        return content;
    };

    useEffect(
        () => () => {
            clearTimeout(hideAnimationTimeout.current);
            clearTimeout(triggerClickTimeout.current);
            clearOutsideClickBlocker();
        },
        []
    );

    useEffect(() => {
        const index = selectedIndex > 1 ? selectedIndex - 1 : 1;
        const selectedElement: HTMLElement = menuContentRef.current?.querySelector(`*:nth-child(${index})`);

        if (selectedElement && menuContentRef.current) {
            menuContentRef.current.scrollTop = selectedElement.offsetTop;
        }
    }, [selectedIndex, isDropdownMenuOpen, expandedDropdown]);

    useEffect(() => {
        if (visibilityChangeCallback) {
            visibilityChangeCallback(isDropdownMenuOpen);
        }
    }, [isDropdownMenuOpen]);

    useEffect(() => {
        setDropdownPosition();
    }, [resolution]);

    useEffect(() => {
        if (!menuContentRef.current) return;

        const naturalHeight = menuContentRef.current.scrollHeight;
        const notExpandedHeight = (menuContentRef.current.children[0]?.clientHeight ?? 48) * 5;
        const expandedHeight = document.body.clientHeight * 0.6;
        const cap = expandedDropdown ? expandedHeight : notExpandedHeight;

        if (naturalHeight <= cap) {
            setScrollHeight(`${naturalHeight}px`);
            setCanExpand(false);
        } else {
            setScrollHeight(`${cap}px`);
            setCanExpand(true);
        }
    }, [expandedDropdown, resolution, isDropdownMenuOpen]);

    useEffect(() => {
        if (!isDropdownMenuOpen) {
            setExpandedDropdown(false);
        }
    }, [isDropdownMenuOpen]);

    const focusTrigger = () => {
        const wrapper = menuButtonRef.current;
        const innerTrigger = Array.from(wrapper?.querySelectorAll<HTMLElement>(TRIGGER_TARGETS) ?? []).find(
            (element) => !menuContentRef.current?.contains(element)
        );

        (innerTrigger ?? wrapper?.parentElement?.closest<HTMLElement>(TRIGGER_TARGETS))?.focus();
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Escape' || !isDropdownMenuOpen) return;

        event.preventDefault();
        event.stopPropagation();
        setIsDropdownMenuOpen(false);
        focusTrigger();
    };

    return (
        <DropdownWrapper
            {...rest}
            onKeyDown={handleKeyDown}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            ref={(ref) => {
                menuButtonRef.current = ref;
            }}
            onClick={(e) => handleClick(e)}
        >
            {triggerButton}
            {isMobile() || isMobileLandscape() ? getMobileView() : getDesktopView()}
        </DropdownWrapper>
    );
};

export type DropdownItemWithSvgProp = {
    svg: string;
    href?: string;
    onClick?: (e) => void;
    hideOnDesktop?: boolean;
    tabindex?: number;
    children?: ReactNode;
};

export const DropdownItemWithSvg: React.FC<DropdownItemWithSvgProp> = ({
    svg = '',
    href = '',
    onClick,
    children,
    hideOnDesktop = false,
    tabindex,
}) => {
    const handleClick = (e) => {
        if (onClick) {
            e.preventDefault();
            onClick(e);
        }
    };
    return (
        <DropdownItemWithSvgWrapper
            as={href ? 'a' : onClick ? 'button' : 'div'}
            type={!href && onClick ? 'button' : undefined}
            href={href || undefined}
            onClick={onClick ? handleClick : undefined}
            hideOnDesktop={hideOnDesktop}
            tabIndex={tabindex}
        >
            <SVGWrapper>
                <SVGInline svg={svg} />
            </SVGWrapper>
            <DropdownLabel>{children}</DropdownLabel>
        </DropdownItemWithSvgWrapper>
    );
};

export type DropdownItemSimpleProp = {
    key?: string | number;
    selected?: boolean;
    href?: string;
    onClick?: (e) => void;
    tabindex?: number;
    children?: ReactNode;
};

export const DropdownItemSimple: React.FC<DropdownItemSimpleProp> = ({ href = '', onClick, children, selected, tabindex }) => {
    const handleClick = (e) => {
        if (onClick) {
            e.preventDefault();
            onClick(e);
        }
    };

    return (
        <DropdownItemSimpleWrapper
            as={href ? 'a' : 'button'}
            type={href ? undefined : 'button'}
            data-test-id={'header-account-dropdownoptions'}
            selected={!!selected}
            href={href || undefined}
            aria-pressed={selected}
            onClick={handleClick}
            tabIndex={tabindex}
        >
            {children}
            {selected && (
                <RightSvgWrapper>
                    <SVGInline className={'right'} svg={icons.checkMarkIcon} />
                </RightSvgWrapper>
            )}
        </DropdownItemSimpleWrapper>
    );
};
